// Экспликация «где …» (4.6.1.7): таблица «символ – расшифровка, единица» с заполнением символами формулы в порядке
// их появления, подсказками расшифровок (ранее в документе и частые обозначения), единицами СИ и предпросмотром
// ровно в том виде, как будет в отчёте (запятая перед «где», «–», «;», «.»).
import { useMemo, useState } from 'react';
import type { WhereItem } from '../../core/schema/math';
import { GREEK, symbols, symKey } from './latex';
import { convertLinear } from './unicodemath';
import { renderTex } from './ml';
import { UNITS } from './templates';
import type { WhereKnown } from './state';

/** Частые обозначения (подсказка расшифровки, если в документе символ ещё не пояснялся). */
const COMMON: Record<string, string> = {
  m: 'масса, кг', V: 'объём, м³', ρ: 'плотность, кг/м³', t: 'время, с', U: 'напряжение, В', I: 'сила тока, А',
  R: 'сопротивление, Ом', P: 'мощность, Вт', F: 'сила, Н', v: 'скорость, м/с', a: 'ускорение, м/с²', f: 'частота, Гц',
  ω: 'угловая частота, рад/с', T: 'постоянная времени, с', k: 'коэффициент передачи', p: 'оператор Лапласа',
  ξ: 'коэффициент демпфирования', τ: 'время запаздывания, с', n: 'число измерений', i: 'номер измерения',
  Q: 'количество теплоты, Дж', c: 'удельная теплоёмкость, Дж/(кг·К)', S: 'площадь, м²', l: 'длина, м', h: 'высота, м',
  E: 'энергия, Дж', A: 'работа, Дж', C: 'ёмкость, Ф', L: 'индуктивность, Гн', φ: 'фаза, рад', σ: 'перерегулирование, %',
  'W(p)': 'передаточная функция', 'W': 'передаточная функция', 'x_{i}': 'i-е измеренное значение', 'x̄': 'среднее значение',
  'Δ': 'абсолютная погрешность', 'δ': 'относительная погрешность, %', 'N': 'число элементов', 'r': 'радиус, м',
  'g': 'ускорение свободного падения, м/с²', 'η': 'коэффициент полезного действия', 'λ': 'длина волны, м',
};

const UNIT_TAIL = /^(?:[А-Яа-яЁёA-Za-zΩµ°%]{1,6}(?:[·⋅/][А-Яа-яЁёA-Za-z()·⋅²³]{1,10})*[²³]?|°C|°С|\([^)]{1,12}\)\/?.*)$/;

/** «масса образца, кг» -> ['масса образца', 'кг'] (если хвост похож на единицу). */
export function splitUnit(desc: string): [string, string] {
  const d = (desc || '').trim().replace(/[;.]\s*$/, '');
  const i = d.lastIndexOf(', ');
  if (i > 0) {
    const tail = d.slice(i + 2).trim();
    if (tail.length <= 14 && UNIT_TAIL.test(tail) && !/\s/.test(tail.replace(/ /g, '')) && !/^[а-яё]{5,}/.test(tail)) return [d.slice(0, i), tail];
  }
  return [d, ''];
}
export const joinUnit = (desc: string, unit: string) => (unit.trim() ? desc.trim().replace(/[,;.]\s*$/, '') + ', ' + unit.trim() : desc.trim());

/** Ввод символа: «rho», «t_i», «ρ», «\bar{t}» -> LaTeX. */
export function symToLatex(s: string): string {
  const t = (s || '').trim();
  if (!t) return '';
  if (/[\\{}]/.test(t)) return t;
  const m = t.match(/^([A-Za-z]+)(_.*)?$/);
  if (m && m[1] in GREEK) return '\\' + m[1] + (m[2] ? convertLinear(m[2]) : '');
  return convertLinear(t);
}

interface Row { sym: string; desc: string; unit: string }
const toRows = (w: WhereItem[]): Row[] => (w || []).map((x) => { const [d, u] = splitUnit(x.desc); return { sym: x.sym, desc: d, unit: u }; });
// строка без символа - строка-определение/значения из импорта .docx («где k = 1, 3, 5…», «$W_1(p)=…$»): сохраняется
export const fromRows = (rows: Row[]): WhereItem[] => rows.filter((r) => r.sym.trim() || r.desc.trim())
  .map((r) => ({ sym: r.sym.trim() ? symToLatex(r.sym) : '', desc: joinUnit(r.desc, r.unit) }));

export function WhereEditor({ latex, value, onChange, known }: {
  latex: string; value: WhereItem[]; onChange: (w: WhereItem[]) => void; known?: Map<string, WhereKnown>;
}) {
  const [rows, setRows] = useState<Row[]>(() => toRows(value));
  const set = (r: Row[]) => { setRows(r); onChange(fromRows(r)); };
  const fsyms = useMemo(() => symbols(latex), [latex]);
  const present = new Set(rows.map((r) => symKey(symToLatex(r.sym))));
  const missing = fsyms.filter((s) => !present.has(symKey(s)));
  const extra = rows.filter((r) => r.sym.trim() && !fsyms.some((s) => symKey(s) === symKey(symToLatex(r.sym))));
  const suggest = (sym: string): string => {
    const k = symKey(symToLatex(sym));
    if (!k) return '';
    const kn = known?.get(k);
    if (kn) return kn.desc;
    return COMMON[k] || COMMON[k.replace(/[_{}]/g, '')] || '';
  };
  const fill = () => {
    const add = missing.map((s) => { const sg = suggest(s); const [d, u] = splitUnit(sg); return { sym: s, desc: known?.has(symKey(s)) ? d : '', unit: known?.has(symKey(s)) ? u : '' }; });
    set([...rows.filter((r) => r.sym.trim() || r.desc.trim()), ...add]);
  };
  const order = () => {
    const pos = (r: Row) => { const i = fsyms.findIndex((s) => symKey(s) === symKey(symToLatex(r.sym))); return i < 0 ? 999 : i; };
    set([...rows].sort((a, b) => pos(a) - pos(b)));
  };
  const upd = (i: number, patch: Partial<Row>) => set(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const move = (i: number, d: number) => { const r = [...rows]; const j = i + d; if (j < 0 || j >= r.length) return; [r[i], r[j]] = [r[j], r[i]]; set(r); };
  const items = fromRows(rows);

  return (
    <div className="mx-where" data-testid="where-editor">
      <div className="mx-where-head">
        <span className="mx-where-title">Экспликация «где …»</span>
        <span className="muted">символы - в порядке появления в формуле, сначала числитель (4.6.1.7)</span>
        <span className="mx-grow" />
        {missing.length > 0 && <button type="button" className="mx-link" onMouseDown={(e) => e.preventDefault()} onClick={fill} data-testid="where-fill">
          Заполнить из формулы ({missing.length})</button>}
        {rows.length > 1 && <button type="button" className="mx-link" onMouseDown={(e) => e.preventDefault()} onClick={order}>Упорядочить как в формуле</button>}
      </div>
      {rows.length > 0 && (
        <div className="mx-where-grid">
          <span className="mx-wh">Символ</span><span /><span className="mx-wh">Расшифровка</span><span className="mx-wh">Единица</span><span />
          {rows.map((r, i) => {
            const sg = !r.desc ? suggest(r.sym) : '';
            const [sgd, sgu] = splitUnit(sg);
            return (
              <div key={i} className="mx-where-row" style={{ display: 'contents' }}>
                <span className="mx-sym">
                  <input className="mono" value={r.sym} placeholder="t_i, rho" onChange={(e) => upd(i, { sym: e.target.value })}
                    title="Символ: t_i, rho, \bar{t}, U_вх" data-testid="where-sym" />
                  <span className="mx-sym-view" dangerouslySetInnerHTML={{ __html: r.sym ? renderTex(symToLatex(r.sym)) : '' }} />
                </span>
                <span className="mx-dash">–</span>
                <span className="mx-desc">
                  <input value={r.desc} placeholder="что обозначает символ" onChange={(e) => upd(i, { desc: e.target.value })}
                    className={r.sym && !r.desc ? 'mx-need' : undefined} title={sgd ? `Подсказка: ${sgd} - Tab или кнопка справа` : undefined}
                    onKeyDown={(e) => { if (e.key === 'Tab' && !r.desc && sgd) { e.preventDefault(); upd(i, { desc: sgd, unit: r.unit || sgu }); } }}
                    data-testid="where-desc" />
                  {sgd && <button type="button" className="mx-sugg" title="Подставить (Tab)" onMouseDown={(e) => e.preventDefault()}
                    onClick={() => upd(i, { desc: sgd, unit: r.unit || sgu })}>↵ {sgd}{sgu ? ', ' + sgu : ''}</button>}
                </span>
                <input className="mx-unit" list="mx-units" value={r.unit} placeholder="—" onChange={(e) => upd(i, { unit: e.target.value })}
                  title="Единица величины (отделяется запятой)" data-testid="where-unit" />
                <span className="mx-row-btns">
                  <button type="button" title="Выше" onClick={() => move(i, -1)}>↑</button>
                  <button type="button" title="Ниже" onClick={() => move(i, 1)}>↓</button>
                  <button type="button" title="Удалить" onClick={() => set(rows.filter((_, j) => j !== i))}>×</button>
                </span>
              </div>
            );
          })}
        </div>
      )}
      <datalist id="mx-units">{UNITS.map((u) => <option key={u} value={u} />)}</datalist>
      <div className="mx-where-foot">
        <button type="button" className="mx-link" onClick={() => set([...rows, { sym: '', desc: '', unit: '' }])} data-testid="where-add">+ символ</button>
        {missing.length > 0 && <span className="muted">Не пояснены: <span dangerouslySetInnerHTML={{ __html: missing.slice(0, 8).map((s) => renderTex(s)).join(', ') }} /></span>}
        {extra.length > 0 && <span className="mx-warn">Нет в формуле: {extra.map((r) => r.sym).join(', ')}</span>}
      </div>
      {items.length > 0 && (
        <div className="mx-where-preview" title="Так экспликация будет выглядеть в отчёте">
          {items.map((w, i) => (
            <div key={i}>{i === 0 ? 'где ' : ''}{w.sym
              ? <><span dangerouslySetInnerHTML={{ __html: renderTex(w.sym) }} /> – {whereText(w.desc)}</> : rawWhereText(w.desc)}{whereEnd(w, i === items.length - 1)}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Строка-определение без символа (импорт: «k = 1, 3, 5…», «$k=10$») как её выведет экспорт: как есть, без конечного
 *  знака (web/server/stoweb/convert/nodes_math.raw_desc). */
export function rawWhereText(desc: string): string {
  const d = (desc || '').replace(/\s+/g, ' ').trim();
  return /(?:…|\.\.\.)$/.test(d) ? d : d.replace(/[\s;.,]+$/, '');
}

/** Знак в конце строки экспликации: «;», у последней «.» (строка-значение с многоточием - без точки). */
export function whereEnd(w: WhereItem, last: boolean): string {
  if (!last) return ';';
  return !w.sym && /(?:…|\.\.\.)$/.test(rawWhereText(w.desc)) ? '' : '.';
}

/** Текст расшифровки так, как её оформит экспорт (строчная буква, без конечного знака). */
export function whereText(desc: string): string {
  let d = (desc || '').replace(/[;.,]\s*$/, '').trim();
  d = d.replace(/^(.*\S)\s*[([]\s*([^()[\]]{1,15})\s*[)\]]$/, (m, a, u) => (/^[A-Za-zА-Яа-яЁёΩµ°%/·⋅\-²³\d ]+$/.test(u) ? a + ', ' + u : m));
  if (d && /^[A-ZА-ЯЁ][a-zа-яё]/.test(d)) d = d[0].toLowerCase() + d.slice(1);
  return d;
}
